/**
 * BenchmarkRangeChart — "What companies like yours pay"
 * Third-party NetSuite cost bands (Vendr, ERP Research) by company size:
 * subscription per year, implementation one-off and first-year total, drawn as
 * floating range bars on a log-scale axis with an HKD/USD toggle.
 *
 * Every figure here is a published third-party benchmark, mostly US buyers.
 * None of it is Oracle pricing and none of it is a PS Global quote. The source
 * name sits under each band label on screen so screenshots keep attribution.
 *
 * Data: components/netsuite-pricing/data/benchmark-bands.json (read 2026-10-02).
 * All visible copy lives in STR so a Traditional Chinese version can be added.
 */
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import data from '@/components/netsuite-pricing/data/benchmark-bands.json';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* ------------------------------- Strings ------------------------------- */
const STR = {
  title: 'What companies like yours pay',
  sub: 'Published third-party benchmarks by company size: subscription, implementation and first year.',
  logoAlt: 'PS Global Consulting',
  currencyGroup: 'Currency',
  hkd: 'HKD',
  usd: 'USD',
  converted: 'converted at 7.8',
  published: 'as published',
  measures: {
    sub: 'Subscription per year',
    impl: 'Implementation',
    total: 'First year',
  },
  median: 'Vendr median buyer',
  notPublished: 'not published',
  reports: 'reports',
  estimate: 'editorial estimate',
  to: 'to',
  caption: 'Third-party benchmarks, mostly US buyers. Not Oracle prices.',
  fxNote: 'HKD converted at the HKMA linked-rate midpoint of 7.8.',
  sources: 'Sources',
  purchases: 'purchases',
  checked: 'Checked 2 Oct 2026.',
  closeTip: 'Press Escape to close',
  bands: {
    erpresearch_small: { label: 'Around 10 users', detail: 'core ERP' },
    vendr_small: { label: '10 to 25 users', detail: 'core financials plus 1 or 2 modules' },
    vendr_mid: { label: '25 to 100 users', detail: 'multiple modules, moderate complexity' },
    vendr_enterprise: { label: '100+ users', detail: 'advanced modules, multi-subsidiary, high transaction volume' },
  } as Record<string, { label: string; detail: string }>,
};

/* -------------------------------- Data --------------------------------- */
type Currency = 'hkd' | 'usd';
type MeasureKey = 'sub' | 'impl' | 'total';
const MEASURES: MeasureKey[] = ['sub', 'impl', 'total'];
const JSON_KEY = {
  sub: 'subscription_per_year',
  impl: 'implementation_one_off',
  total: 'first_year_total',
} as const;

interface Figure {
  usdLow: number; usdHigh: number; hkdLow: number; hkdHigh: number;
  open: boolean; verbatim: string;
}
interface Band {
  id: string; label: string; detail: string; source: string; url: string;
  estimate: boolean; figures: Record<MeasureKey, Figure | null>;
}

const FX = data.meta.fx.rate_used;

const BANDS: Band[] = data.bands.map((b) => {
  // The verbatim list is in measure order, skipping measures the source does not publish.
  let vi = 0;
  const figures = {} as Record<MeasureKey, Figure | null>;
  MEASURES.forEach((m) => {
    const f = b[JSON_KEY[m]];
    if (!f) { figures[m] = null; return; }
    figures[m] = {
      usdLow: f.usd_low, usdHigh: f.usd_high, hkdLow: f.hkd_low, hkdHigh: f.hkd_high,
      open: f.open_ended_top, verbatim: b.verbatim[vi++] ?? '',
    };
  });
  const fallback = b.profile_label_as_source_defines_it.split(/,\s*/);
  const named = STR.bands[b.id] ?? { label: fallback[0], detail: fallback.slice(1).join(', ') };
  return {
    id: b.id, label: named.label, detail: named.detail,
    source: b.source_name, url: b.source_url,
    estimate: b.confidence.startsWith('medium'), figures,
  };
});

const MEDIAN = {
  usd: data.vendr_dataset.median_usd_per_year,
  hkd: data.vendr_dataset.median_hkd_per_year,
  n: data.vendr_dataset.purchases,
  verbatim: data.vendr_dataset.verbatim,
  source: 'Vendr',
  url: 'https://www.vendr.com/marketplace/netsuite',
};
// Per the dataset note: the median comes from Vendr's transaction data, whose
// stated high (US$299k) sits below the enterprise band, so mark small/mid only.
const MEDIAN_BANDS = new Set(['vendr_small', 'vendr_mid']);

const VENDR = BANDS.find((b) => b.source === 'Vendr') ?? BANDS[1];
const ERP = BANDS.find((b) => b.source === 'ERP Research') ?? BANDS[0];

// Log-scale domain in USD (the published unit); HKD is positioned via /7.8.
const DOMAIN = { lo: 8000, hi: 3500000 };
const TICKS: Record<Currency, number[]> = {
  usd: [10000, 100000, 1000000],
  hkd: [100000, 1000000, 10000000],
};
const pos = (usd: number): number =>
  ((Math.log(usd) - Math.log(DOMAIN.lo)) / (Math.log(DOMAIN.hi) - Math.log(DOMAIN.lo))) * 100;
const toUsd = (v: number, cur: Currency): number => (cur === 'hkd' ? v / FX : v);

const fmt = (v: number, cur: Currency): string => {
  const s = cur === 'hkd' ? 'HK$' : 'US$';
  if (v >= 1e6) return `${s}${(v / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  return `${s}${Math.round(v / 1000)}k`;
};
const withCommas = (v: number): string => Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtFull = (v: number, cur: Currency): string => `${cur === 'hkd' ? 'HK$' : 'US$'}${withCommas(v)}`;

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'bmr-styles';
const CSS = `
.bmr-root { --bmr-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --teal-900: #04445c; --gold: #B08A2E; --gold-deep: #7A5F1D;
  position: relative; box-sizing: border-box; width: 100%; max-width: 860px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--bmr-font); -webkit-font-smoothing: antialiased; }
.bmr-root *, .bmr-root *::before, .bmr-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.bmr-root p { letter-spacing: normal; }
.bmr-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.bmr-root .bmr-logo { height: 40px; width: auto; flex: none; }
.bmr-root h2 { font-size: 26px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; max-width: 560px; color: var(--ink); }
.bmr-root .sub { font-size: 14px; color: var(--body); margin-top: 8px; max-width: 620px; line-height: 1.55; }
.bmr-root .controls { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px 24px; flex-wrap: wrap; margin-top: 22px; }
.bmr-root .legend { display: flex; gap: 14px 18px; flex-wrap: wrap; font-size: 12px; color: var(--ink-2); line-height: 1.4; }
.bmr-root .legend span { display: inline-flex; align-items: center; }
.bmr-root .legend i { display: inline-block; width: 18px; height: 8px; border-radius: 2px; margin-right: 7px; flex: none; }
.bmr-root .legend .k-sub i { background: var(--teal); }
.bmr-root .legend .k-impl i { background: var(--teal-deep); }
.bmr-root .legend .k-total i { background: var(--ink); }
.bmr-root .legend .k-med i { width: 8px; height: 8px; border-radius: 50%; background: var(--gold); margin-left: 5px; margin-right: 12px; }
.bmr-root .cur { display: flex; flex-direction: column; align-items: flex-end; }
.bmr-root .seg { display: inline-flex; border: 1px solid var(--line); border-radius: 999px; padding: 3px; gap: 2px; background: var(--paper); }
.bmr-root .seg button { border: 0; background: none; font: 600 12px var(--bmr-font); padding: 6px 13px; border-radius: 999px; color: var(--ink-2); cursor: pointer; transition: background .15s, color .15s; line-height: 1.2; }
.bmr-root .seg button[aria-pressed="true"] { background: var(--teal-900); color: #fff; }
.bmr-root .seg button:focus-visible { outline: 2px solid var(--teal); outline-offset: 1px; }
.bmr-root .fxnote { font-size: 11px; color: var(--muted); text-align: right; margin-top: 6px; line-height: 1.3; }
.bmr-root .chart { margin-top: 14px; display: grid; grid-template-columns: minmax(136px, 176px) minmax(0, 1fr); column-gap: 20px; }
.bmr-root .lbl { font-size: 13.5px; font-weight: 600; line-height: 1.25; padding: 14px 0 0; border-top: 1px solid var(--line); color: var(--ink); }
.bmr-root .lbl small { display: block; font-weight: 400; color: var(--muted); font-size: 11px; margin-top: 3px; line-height: 1.4; }
.bmr-root .lbl .src { display: block; margin-top: 8px; font-size: 10.5px; font-weight: 600; color: var(--teal-deep); letter-spacing: .06em; text-transform: uppercase; }
.bmr-root .lbl .src em { font-style: normal; color: var(--muted); font-weight: 400; text-transform: none; letter-spacing: 0; }
.bmr-root .track { position: relative; height: 90px; border-top: 1px solid var(--line); padding-top: 13px; }
.bmr-root .grid { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--line); transition: left .45s cubic-bezier(.2,.8,.2,1); }
.bmr-root .row { position: relative; height: 18px; margin-bottom: 5px; }
.bmr-root .bar { position: absolute; top: 2px; height: 14px; cursor: default; border-radius: 3px; transition: left .45s cubic-bezier(.2,.8,.2,1), width .45s cubic-bezier(.2,.8,.2,1); }
.bmr-root .bar::before { content: ""; position: absolute; inset: 0; border-radius: 3px; background: var(--teal); transition: filter .15s; }
.bmr-root .bar.impl::before { background: var(--teal-deep); }
.bmr-root .bar.total::before { background: var(--ink); }
.bmr-root .bar.open::before { border-top-right-radius: 0; border-bottom-right-radius: 0; -webkit-mask: linear-gradient(90deg, #000 68%, transparent); mask: linear-gradient(90deg, #000 68%, transparent); }
.bmr-root .bar:hover::before, .bmr-root .bar:focus-visible::before { filter: brightness(1.2); }
.bmr-root .bar:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
.bmr-root .val { position: absolute; top: 50%; transform: translateY(-50%); font-size: 10.5px; font-weight: 600; color: var(--ink-2); white-space: nowrap; font-variant-numeric: tabular-nums; pointer-events: none; line-height: 1; }
.bmr-root .val.lo { right: calc(100% + 6px); }
.bmr-root .val.hi { left: calc(100% + 6px); }
.bmr-root .val.range { display: none; left: calc(100% + 6px); }
.bmr-root .bar.combined .val.lo, .bmr-root .bar.combined .val.hi { display: none; }
.bmr-root .bar.combined .val.range { display: block; }
.bmr-root .bar.flip .val.range { left: auto; right: calc(100% + 6px); }
.bmr-root .na { position: absolute; top: 2px; height: 14px; left: 0; width: 38%; min-width: 150px; max-width: 100%; border: 1px dashed #c9d0d4; border-radius: 3px; display: flex; align-items: center; padding: 0 8px; font-size: 10.5px; color: var(--muted); white-space: nowrap; overflow: hidden; }
.bmr-root .med { position: absolute; top: -3px; height: 24px; width: 2px; background: var(--gold); transition: left .45s cubic-bezier(.2,.8,.2,1); cursor: default; z-index: 2; }
.bmr-root .med::before { content: ""; position: absolute; left: -3px; top: -5px; width: 8px; height: 8px; border-radius: 50%; background: var(--gold); }
.bmr-root .med:focus-visible { outline: 2px solid var(--gold); outline-offset: 4px; }
.bmr-root .axis { position: relative; height: 18px; grid-column: 2; margin-top: 2px; }
.bmr-root .axis span { position: absolute; transform: translateX(-50%); font-size: 10.5px; color: var(--muted); font-variant-numeric: tabular-nums; transition: left .45s cubic-bezier(.2,.8,.2,1); white-space: nowrap; }
.bmr-root .foot { margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--line); }
.bmr-root .foot .cap { font-size: 12.5px; line-height: 1.6; color: var(--body); max-width: 720px; }
.bmr-root .foot .cap b { font-weight: 600; color: var(--ink); }
.bmr-root .foot a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.bmr-root .foot a:hover { color: var(--teal); }
.bmr-root .tip { position: absolute; z-index: 5; width: 300px; max-width: calc(100% - 16px); background: var(--ink); color: #fff; border-radius: 10px; padding: 10px 12px; font-size: 12px; line-height: 1.5; pointer-events: none; opacity: 0; transform: translateY(4px); transition: opacity .15s, transform .15s; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); }
.bmr-root .tip.on { opacity: 1; transform: none; }
.bmr-root .tip b { display: block; color: #A9D9D4; font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 4px; font-weight: 600; }
.bmr-root .tip i { display: block; color: #A9B4B8; font-style: normal; margin-top: 6px; font-size: 11px; }
.bmr-root.compact { padding: 24px 16px 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.bmr-root.compact .bmr-logo { display: none; }
.bmr-root.compact h2 { font-size: 21px; }
.bmr-root.compact .sub { font-size: 13px; }
.bmr-root.compact .controls { flex-direction: column; align-items: flex-start; margin-top: 18px; }
.bmr-root.compact .cur { align-items: flex-start; }
.bmr-root.compact .fxnote { text-align: left; }
.bmr-root.compact .chart { grid-template-columns: 1fr; column-gap: 0; }
.bmr-root.compact .lbl { padding-bottom: 8px; }
.bmr-root.compact .track { border-top: 0; padding-top: 4px; height: 80px; }
.bmr-root.compact .axis { grid-column: 1; }
.bmr-root.compact .na { width: 60%; min-width: 0; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ------------------------------ Component ------------------------------ */
interface Tip { node: React.ReactNode; x: number; y: number; }
export interface BenchmarkRangeChartProps { compact?: boolean; currency?: Currency; }

const BenchmarkRangeChart: React.FC<BenchmarkRangeChartProps> = ({ compact = false, currency = 'hkd' }) => {
  useInjectedStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const [cur, setCur] = useState<Currency>(currency);
  const [narrow, setNarrow] = useState(false);
  const [tip, setTip] = useState<Tip | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const isCompact = compact || narrow;

  useEffect(() => { setCur(currency); }, [currency]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setNarrow(el.offsetWidth < 600);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  const showTip = useCallback((el: HTMLElement, node: React.ReactNode): void => {
    const root = rootRef.current;
    if (!root) return;
    const c = root.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    const w = Math.min(300, c.width - 16);
    const x = Math.max(8, Math.min(c.width - w - 8, e.left - c.left + e.width / 2 - w / 2));
    const y = e.bottom - c.top + 8;
    setTip({ node, x, y });
    setTipOpen(true);
  }, []);
  const hideTip = useCallback((): void => setTipOpen(false), []);
  const onKey = (e: React.KeyboardEvent): void => { if (e.key === 'Escape') hideTip(); };

  const ticks = TICKS[cur];
  const fxLabel = cur === 'hkd' ? STR.converted : STR.published;

  return (
    <div className={`bmr-root${isCompact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div>
          <h2>{STR.title}</h2>
          <p className="sub">{STR.sub}</p>
        </div>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="bmr-logo" src={PSGLOBAL_LOGO} alt={STR.logoAlt} />
      </div>

      <div className="controls">
        <div className="legend" aria-hidden="true">
          {MEASURES.map((m) => <span className={`k-${m}`} key={m}><i></i>{STR.measures[m]}</span>)}
          <span className="k-med"><i></i>{STR.median}</span>
        </div>
        <div className="cur">
          <div className="seg" role="group" aria-label={STR.currencyGroup}>
            <button type="button" aria-pressed={cur === 'hkd'} onClick={() => setCur('hkd')}>{STR.hkd}</button>
            <button type="button" aria-pressed={cur === 'usd'} onClick={() => setCur('usd')}>{STR.usd}</button>
          </div>
          <p className="fxnote" aria-live="polite">{cur.toUpperCase()} {fxLabel}</p>
        </div>
      </div>

      <div className="chart">
        {BANDS.map((b) => (
          <React.Fragment key={b.id}>
            <div className="lbl">
              {b.label}
              <small>{b.detail}</small>
              <span className="src">{b.source}{b.estimate && <em> · {STR.estimate}</em>}</span>
            </div>
            <div className="track">
              {ticks.map((t) => <span className="grid" key={t} style={{ left: `${pos(toUsd(t, cur))}%` }}></span>)}
              {MEASURES.map((m) => {
                const f = b.figures[m];
                if (!f) {
                  return (
                    <div className="row" key={m}>
                      <div className="na">{STR.measures[m]}: {STR.notPublished}</div>
                    </div>
                  );
                }
                const lo = pos(f.usdLow);
                const hi = pos(f.usdHigh);
                const loTxt = fmt(cur === 'hkd' ? f.hkdLow : f.usdLow, cur);
                const hiTxt = fmt(cur === 'hkd' ? f.hkdHigh : f.usdHigh, cur) + (f.open ? '+' : '');
                // Wide: lo/hi labels flank the bar. Narrow, or when the bar ends near
                // the right edge: one combined label, flipped to the left if needed.
                const combined = isCompact || hi > 86;
                const flip = combined && hi > (isCompact ? 62 : 86);
                const cls = `bar ${m}${f.open ? ' open' : ''}${combined ? ' combined' : ''}${flip ? ' flip' : ''}`;
                const aria = `${b.source} ${STR.reports}: ${STR.measures[m]} ${loTxt} ${STR.to} ${hiTxt}, ${b.label}`;
                const node = (
                  <>
                    <b>{b.source} {STR.reports} · {STR.measures[m]}</b>
                    &ldquo;{f.verbatim}&rdquo;
                    <i>{b.label}{b.estimate ? ` · ${STR.estimate}` : ''}</i>
                  </>
                );
                const showMedian = m === 'sub' && MEDIAN_BANDS.has(b.id);
                const medianNode = (
                  <>
                    <b>{MEDIAN.source} {STR.reports}</b>
                    &ldquo;{MEDIAN.verbatim}&rdquo;
                    <i>{STR.median}: {fmtFull(cur === 'hkd' ? MEDIAN.hkd : MEDIAN.usd, cur)} ({cur === 'hkd' ? STR.converted : STR.published})</i>
                  </>
                );
                return (
                  <div className="row" key={m}>
                    <div
                      className={cls}
                      tabIndex={0}
                      role="img"
                      aria-label={aria}
                      style={{ left: `${lo}%`, width: `${hi - lo}%` }}
                      onMouseEnter={(e) => showTip(e.currentTarget, node)}
                      onFocus={(e) => showTip(e.currentTarget, node)}
                      onClick={(e) => showTip(e.currentTarget, node)}
                      onMouseLeave={hideTip}
                      onBlur={hideTip}
                      onKeyDown={onKey}
                    >
                      <span className="val lo">{loTxt}</span>
                      <span className="val hi">{hiTxt}</span>
                      <span className="val range">{loTxt} {STR.to} {hiTxt}</span>
                    </div>
                    {showMedian && (
                      <div
                        className="med"
                        tabIndex={0}
                        role="img"
                        aria-label={`${STR.median}: ${fmtFull(cur === 'hkd' ? MEDIAN.hkd : MEDIAN.usd, cur)}`}
                        style={{ left: `${pos(MEDIAN.usd)}%` }}
                        onMouseEnter={(e) => showTip(e.currentTarget, medianNode)}
                        onFocus={(e) => showTip(e.currentTarget, medianNode)}
                        onClick={(e) => showTip(e.currentTarget, medianNode)}
                        onMouseLeave={hideTip}
                        onBlur={hideTip}
                        onKeyDown={onKey}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        ))}
        <div className="axis" aria-hidden="true">
          {ticks.map((t) => <span key={t} style={{ left: `${pos(toUsd(t, cur))}%` }}>{fmt(t, cur)}</span>)}
        </div>
      </div>

      <div className="foot">
        <p className="cap">
          <b>{STR.caption}</b> {STR.fxNote} {STR.sources}:{' '}
          <a href={VENDR.url} target="_blank" rel="noopener noreferrer">{VENDR.source}</a> ({withCommas(MEDIAN.n)} {STR.purchases}) ·{' '}
          <a href={ERP.url} target="_blank" rel="noopener noreferrer">{ERP.source}</a>. {STR.checked}
        </p>
      </div>

      <div className={`tip${tipOpen ? ' on' : ''}`} role="tooltip" style={tip ? { left: tip.x, top: tip.y } : undefined}>
        {tip ? tip.node : null}
      </div>
    </div>
  );
};

export default BenchmarkRangeChart;
